import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { FreezeWindow } from '@goaltree/backend/convex/lib/goalFreeze'
import type { TimeBudget } from '@goaltree/backend/convex/lib/timeBudget'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import { Sheet, SheetContent } from '@goaltree/ui/components/sheet'
import { cn } from '@goaltree/ui/lib/utils'
import { useAction, useMutation, useQuery } from 'convex/react'
import {
  CalendarDays,
  Heart,
  ImageIcon,
  Layers,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
  Share2,
  Snowflake,
  Sparkles,
  Trash2,
  Trophy,
  Upload,
  Video,
  X,
} from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  AskCoachComposer,
  AskCoachThread,
  askChatActive,
  useAskChips,
  useCoachPersona,
} from '@/components/ask-coach'
import {
  FreezeGoalDialog,
  FrozenBanner,
  goalFreezeState,
  useGoalFreezeActions,
} from '@/components/goal-freeze'
import { GoalTimeBudget } from '@/components/goal-time-budget'
import { InlineEditor, type InlineEditorHandle } from '@/components/inline-editor'
import { SavedHint, useSavedHint } from '@/components/saved-hint'
import { ShareGoalDialog } from '@/components/share-goal-dialog'
import { TaskStatusIcon } from '@/components/task-status-icon'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'
import { useStickToBottom } from '@/hooks/use-stick-to-bottom'
import { useTaskExplain } from '@/hooks/use-task-explain'
import { CATEGORY_COLORS, categoryTextColor } from '@/lib/category-color'
import { patchGoal } from '@/lib/goals-optimistic'
import { completeNode, optimisticRename } from '@/lib/nodes-optimistic'
import { openShareCard } from '@/lib/share-card'
import { playDoneSound } from '@/lib/sounds'
import { taskTimeLabel } from '@/lib/task-time'

export type GoalSheetSummary = {
  done: number
  total: number
  next: { _id: Id<'nodes'>; title: string; milestoneTitle: string; effort: string } | null
  milestones: { _id: string; title: string; done: number; total: number }[]
}

export type GoalDetailSheetData = {
  _id: Id<'nodes'>
  title: string
  category: string
  status?: string
  canEdit?: boolean
  isOwner?: boolean
  goalSlug?: string
  isReadOnly?: boolean
  isPublic?: boolean
  coverUrl?: string | null
  allDone?: boolean
  replanPending?: boolean
  generatingCover?: boolean
  generatingCoverVideo?: boolean
  summary?: GoalSheetSummary
  timeBudget?: TimeBudget
  onReplan?: () => void
  onGenerateCover?: () => void
  onGenerateCoverVideo?: () => void
  onRemoveCover?: () => void
  onUploadCover?: () => void
  onDelete?: () => void
  onOpenMilestone?: (milestoneId: string) => void
  onOpenTask?: (taskId: string) => void
}

export function GoalDetailSheet({
  goal,
  onClose,
}: {
  goal: GoalDetailSheetData | null
  onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const CHIPS = useAskChips('goal')

  const color = goal ? (CATEGORY_COLORS[goal.category?.toLowerCase()] ?? '#6b6b6b') : '#6b6b6b'
  const { isPro } = useIsPro()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [upgradeReason, setUpgradeReason] = useState<'ask-ai' | 'replan' | 'edit-goal'>('ask-ai')
  const chat = useTaskExplain()
  const { ask, isLoading, streamedText, isStreaming, reset } = chat
  const scrollRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<InlineEditorHandle>(null)

  // Status follows progress; the only manual call is "achieved" (done early).
  const [achieved, setAchieved] = useState(goal?.status === 'done')
  const setGoalStatus = useMutation(api.nodes.setGoalStatus).withOptimisticUpdate(
    (store, { goalId, status }) => patchGoal(store, goalId, { status }),
  )
  const rename = useMutation(api.nodes.rename).withOptimisticUpdate(optimisticRename)
  const titleHint = useSavedHint()
  const generateTransformation = useAction(api.visionBoardActions.generateTransformation)
  const [transforming, setTransforming] = useState(false)
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((store, { taskId }) =>
    completeNode(store, taskId),
  )
  const [shareOpen, setShareOpen] = useState(false)

  const { voice, name: coachName } = useCoachPersona(!!goal)
  const goals = useQuery(api.nodes.getUserGoals, goal ? {} : 'skip') as
    | Array<{ _id: string; projectedFinishAt?: number; freezes?: FreezeWindow[] }>
    | undefined
  const listed = goals?.find((g) => g._id === goal?._id)
  const projectedFinishAt = listed?.projectedFinishAt
  const { frozen, until: frozenUntil } = goalFreezeState(listed?.freezes)
  const [freezeOpen, setFreezeOpen] = useState(false)
  const freezeActions = useGoalFreezeActions((goal?._id ?? '') as Id<'nodes'>)
  const likers = useQuery(api.nodes.getGoalLikers, goal ? { goalId: goal._id } : 'skip') as
    | Array<{ userId: string; name: string; image: string | null; handle: string | null }>
    | undefined

  useEffect(() => {
    reset()
    setAchieved(goal?.status === 'done')
  }, [goal?._id])

  const onScrollAreaScroll = useStickToBottom(scrollRef, streamedText, chat.pendingQuestion)

  function send(text: string) {
    if (!goal || !text.trim() || isLoading || isStreaming) return
    if (!isPro) {
      setUpgradeReason('ask-ai')
      setUpgradeOpen(true)
      return
    }
    ask(goal.title, goal.title, text.trim(), goal._id)
  }

  // Hold the ticked task on the card briefly (check + strike) before the next one
  // slides in, so the tap visibly lands.
  const [completing, setCompleting] = useState<GoalSheetSummary['next']>(null)
  function completeNext(task: NonNullable<GoalSheetSummary['next']>) {
    setCompleting(task)
    playDoneSound()
    void complete({ taskId: task._id }).catch(() => setCompleting(null))
    setTimeout(() => setCompleting((c) => (c?._id === task._id ? null : c)), 900)
  }

  function setDone(done: boolean) {
    if (!goal || done === achieved) return
    apply(done)
    if (done)
      toast.success(t('goalSheet.achievedToast'), {
        // Not setDone: its closure still sees "not achieved" and would bail.
        action: { label: t('common.undo'), onClick: () => apply(false) },
      })
  }

  function apply(done: boolean) {
    if (!goal) return
    setAchieved(done)
    const status = done ? 'done' : (summary?.done ?? 0) > 0 ? 'in-progress' : 'backlog'
    setGoalStatus({ goalId: goal._id, status }).catch(() => setAchieved(!done))
  }

  function gated(reason: 'replan' | 'edit-goal', fn?: () => void) {
    if (!isPro) {
      setUpgradeReason(reason)
      setUpgradeOpen(true)
      return
    }
    fn?.()
  }

  const chatting = askChatActive(chat)
  const summary = goal?.summary
  const pct = summary && summary.total ? Math.round((summary.done / summary.total) * 100) : 0
  const canEdit = !!goal?.canEdit
  return (
    <>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason={upgradeReason} />
      <Sheet open={!!goal} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          showCloseButton={false}
          className="flex w-[22rem] flex-col gap-0 p-0 sm:max-w-sm"
        >
          {goal && (
            <div className="flex flex-1 flex-col overflow-hidden">
              <div
                ref={scrollRef}
                onScroll={onScrollAreaScroll}
                className="flex flex-1 flex-col overflow-y-auto"
              >
                <div className="relative flex flex-col gap-3.5 px-4 pt-4 pb-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-1.5 text-[10.5px] font-extrabold uppercase tracking-[0.14em]">
                        <span style={{ color: categoryTextColor(color) }}>
                          {t(`goals.cat.${goal.category?.toLowerCase()}`, {
                            defaultValue: goal.category,
                          })}
                        </span>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="text-muted-foreground">{t('goals.typeLabel')}</span>
                      </p>
                      <InlineEditor
                        ref={titleRef}
                        value={goal.title}
                        onSave={async (v) => {
                          await titleHint.track(rename({ nodeId: goal._id, title: v }))
                        }}
                        disabled={!canEdit || goal.isReadOnly === true}
                        className={cn(
                          'mt-1 block font-black leading-tight tracking-tight text-foreground text-balance',
                          chatting ? 'text-lg' : 'text-[22px]',
                        )}
                      />
                      <SavedHint status={titleHint.status} className="mt-0.5" />
                    </div>
                    <div className="-mr-1.5 flex shrink-0 items-center gap-0.5">
                      {(canEdit || goal.isOwner) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            aria-label={t('goalSheet.more')}
                            className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:bg-muted"
                          >
                            <MoreHorizontal className="size-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56">
                            {canEdit && (
                              <>
                                <DropdownMenuItem
                                  disabled={goal.generatingCover}
                                  onClick={() => gated('edit-goal', goal.onGenerateCover)}
                                >
                                  <ImageIcon className="size-4" />
                                  {goal.generatingCover
                                    ? t('common.generatingCover')
                                    : t('common.generateCoverImage')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={goal.generatingCoverVideo}
                                  onClick={() => gated('edit-goal', goal.onGenerateCoverVideo)}
                                >
                                  <Video className="size-4" />
                                  {goal.generatingCoverVideo
                                    ? t('common.generatingCoverVideo')
                                    : t('common.generateCoverVideo')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={transforming}
                                  onClick={() =>
                                    gated('edit-goal', () => {
                                      setTransforming(true)
                                      generateTransformation({ goalId: goal._id })
                                        .then(() => toast.success(t('common.transformationReady')))
                                        .catch(() => toast.error(t('common.transformationFailed')))
                                        .finally(() => setTransforming(false))
                                    })
                                  }
                                >
                                  <Sparkles className="size-4" />
                                  {transforming
                                    ? t('common.generatingTransformation')
                                    : t('common.generateTransformation')}
                                </DropdownMenuItem>
                                {goal.onUploadCover && (
                                  <DropdownMenuItem
                                    onClick={() => gated('edit-goal', goal.onUploadCover)}
                                  >
                                    <Upload className="size-4" />
                                    {t('common.uploadCover')}
                                  </DropdownMenuItem>
                                )}
                                {goal.coverUrl && goal.onRemoveCover && (
                                  <DropdownMenuItem onClick={() => goal.onRemoveCover?.()}>
                                    <X className="size-4" />
                                    {t('common.removeCover')}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                  onClick={() =>
                                    setTimeout(() => titleRef.current?.startEdit(), 50)
                                  }
                                >
                                  <span className="size-4 text-center text-xs leading-4">Aa</span>
                                  {t('common.rename')}
                                </DropdownMenuItem>
                              </>
                            )}
                            {canEdit && (
                              <DropdownMenuItem onClick={() => setDone(!achieved)}>
                                {achieved ? (
                                  <RotateCcw className="size-4" />
                                ) : (
                                  <Trophy className="size-4 text-amber-500" />
                                )}
                                {t(achieved ? 'goalSheet.reopen' : 'goalSheet.markAchieved')}
                              </DropdownMenuItem>
                            )}
                            {goal.isOwner && achieved && (
                              <DropdownMenuItem
                                onClick={() => openShareCard({ type: 'goal', ref: goal._id })}
                              >
                                <Share2 className="size-4" />
                                {t('shareCard.shareCardMenu')}
                              </DropdownMenuItem>
                            )}
                            {goal.isOwner && !achieved && (
                              <DropdownMenuItem
                                onClick={() =>
                                  frozen ? void freezeActions.unfreeze() : setFreezeOpen(true)
                                }
                              >
                                <Snowflake className="size-4 text-sky-500" />
                                {t(frozen ? 'goalFreeze.unfreeze' : 'goalFreeze.freeze')}
                              </DropdownMenuItem>
                            )}
                            {goal.isOwner && goal.onDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  variant="destructive"
                                  onClick={() => {
                                    onClose()
                                    goal.onDelete?.()
                                  }}
                                >
                                  <Trash2 className="size-4" />
                                  {t('common.delete')}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="flex size-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  </div>

                  {!chatting && achieved && goal.isOwner && (
                    <>
                      <ShareAsTemplate goalId={goal._id} />
                      <button
                        type="button"
                        onClick={() => openShareCard({ type: 'goal', ref: goal._id })}
                        className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-border/60 px-3 py-2 text-xs font-bold text-foreground transition-colors hover:bg-muted"
                      >
                        <Share2 className="size-3.5" />
                        {t('shareCard.shareGoal')}
                      </button>
                    </>
                  )}
                  <AnimatePresence initial={false}>
                    {!chatting && frozen && goal.isOwner && (
                      <motion.div
                        key="frozen"
                        initial={{ opacity: 0, height: 0, scale: 0.97 }}
                        animate={{ opacity: 1, height: 'auto', scale: 1 }}
                        exit={{ opacity: 0, height: 0, filter: 'blur(3px)' }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <FrozenBanner goalId={goal._id} until={frozenUntil} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                  {goal.isOwner && (
                    <FreezeGoalDialog
                      goalId={goal._id}
                      open={freezeOpen}
                      onOpenChange={setFreezeOpen}
                    />
                  )}
                  {!chatting && (
                    <div className="flex flex-wrap items-center gap-2">
                      {achieved && (
                        <span className="flex items-center gap-1.5 rounded-full bg-amber-400/15 px-2.5 py-1 text-xs font-bold text-amber-600 dark:text-amber-300">
                          <Trophy className="size-3.5" />
                          {t('goalSheet.achieved')}
                        </span>
                      )}
                      {goal.isOwner && !frozen && !achieved && (
                        <button
                          type="button"
                          onClick={() => setFreezeOpen(true)}
                          className="flex cursor-pointer items-center gap-1 rounded-full border border-sky-300/50 px-2.5 py-1 text-xs font-bold text-sky-600 transition-colors hover:bg-sky-400/10 dark:border-sky-300/30 dark:text-sky-300"
                        >
                          <Snowflake className="size-3.5" strokeWidth={2.4} />
                          {t('goalFreeze.freezeShort')}
                        </button>
                      )}
                      {projectedFinishAt && !achieved && (
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <CalendarDays className="size-3.5" />
                          {t('goalSheet.onPace', {
                            date: new Date(projectedFinishAt).toLocaleDateString(i18n.language, {
                              month: 'short',
                              day: 'numeric',
                            }),
                          })}
                        </span>
                      )}
                      {likers && likers.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Heart className="size-3" />
                          {likers.length}
                        </span>
                      )}
                    </div>
                  )}

                  {summary && summary.total > 0 && (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex justify-between text-xs tabular-nums text-muted-foreground">
                        <span>
                          {t('goalSheet.tasksOf', { done: summary.done, total: summary.total })}
                        </span>
                        <span>{pct}%</span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-[width] duration-500"
                          style={{ width: `${Math.max(pct, 2)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {!chatting && canEdit && (goal.onReplan || (goal.isOwner && goal.goalSlug)) && (
                    <div className="flex gap-2">
                      {goal.onReplan && (
                        <button
                          type="button"
                          disabled={goal.allDone || goal.replanPending}
                          onClick={() => gated('replan', goal.onReplan)}
                          className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-border/70 bg-card px-3 py-2 text-[12.5px] font-semibold transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-40"
                        >
                          <RefreshCw
                            className={cn(
                              'size-3.5 text-muted-foreground',
                              goal.replanPending && 'animate-spin',
                            )}
                          />
                          {t('common.replanGoal')}
                        </button>
                      )}
                      {goal.isOwner && goal.goalSlug && (
                        <button
                          type="button"
                          onClick={() => setShareOpen(true)}
                          className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-border/70 bg-card px-3 py-2 text-[12.5px] font-semibold transition-colors hover:bg-muted"
                        >
                          <Share2 className="size-3.5 text-muted-foreground" />
                          {t('shareModal.share')}
                        </button>
                      )}
                    </div>
                  )}

                  {!chatting && canEdit && !achieved && !goal.allDone && (
                    <GoalTimeBudget
                      goalId={goal._id}
                      budget={goal.timeBudget}
                      replanPending={goal.replanPending}
                      gate={(fn) => gated('replan', fn)}
                    />
                  )}

                  {!chatting && (completing ?? summary?.next) && (
                    <section className="flex flex-col gap-1.5">
                      <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-muted-foreground/70">
                        {t('goals.upNext')}
                      </p>
                      {(() => {
                        const task = (completing ?? summary?.next)!
                        const done = completing?._id === task._id
                        return (
                          <div
                            key={task._id}
                            role="button"
                            tabIndex={0}
                            onClick={() => !done && goal.onOpenTask?.(task._id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !done) goal.onOpenTask?.(task._id)
                            }}
                            className={cn(
                              'flex cursor-pointer items-center gap-3 rounded-xl border border-primary/30 bg-primary/8 px-3 py-2.5 transition-[opacity,background-color] duration-300 animate-in fade-in slide-in-from-bottom-1 hover:bg-primary/12',
                              done && 'opacity-60',
                            )}
                          >
                            <button
                              type="button"
                              disabled={!canEdit || done}
                              aria-label={t('today.markDone', { defaultValue: 'Mark done' })}
                              onClick={(e) => {
                                e.stopPropagation()
                                completeNext(task)
                              }}
                              className="shrink-0 cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-default"
                            >
                              <span className={cn('block', done && 'animate-[pop_0.35s_ease-out]')}>
                                <TaskStatusIcon status={done ? 'done' : 'pending'} size={18} />
                              </span>
                            </button>
                            <div className="min-w-0 flex-1">
                              <p
                                className={cn(
                                  'truncate text-[13.5px] font-semibold transition-colors',
                                  done && 'text-muted-foreground line-through',
                                )}
                              >
                                {task.title}
                              </p>
                              <p className="truncate font-mono text-[11px] text-muted-foreground">
                                {task.milestoneTitle}
                              </p>
                            </div>
                            <span className="shrink-0 font-mono text-[11px] text-muted-foreground/70">
                              {taskTimeLabel(task, '')}
                            </span>
                          </div>
                        )
                      })()}
                    </section>
                  )}

                  {!chatting && summary && summary.milestones.length > 0 && (
                    <section className="flex flex-col gap-1.5">
                      <p className="flex justify-between text-[10px] font-extrabold uppercase tracking-[0.14em] text-muted-foreground/70">
                        {t('goalSheet.milestones')}
                      </p>
                      <div className="overflow-hidden rounded-xl border border-border/70">
                        {(() => {
                          const current = summary.milestones.findIndex((m) => m.done < m.total)
                          return summary.milestones.map((m, i) => {
                            const isDone = m.total > 0 && m.done === m.total
                            return (
                              <button
                                key={m._id}
                                type="button"
                                onClick={() => goal.onOpenMilestone?.(m._id)}
                                className="flex w-full cursor-pointer items-center gap-2.5 border-b border-border/70 px-3 py-2 text-left text-[13px] transition-colors last:border-b-0 hover:bg-muted/40"
                              >
                                <span
                                  className={cn(
                                    'flex size-5 shrink-0 items-center justify-center rounded-md text-[10.5px] font-extrabold',
                                    i === current
                                      ? 'bg-primary text-primary-foreground'
                                      : isDone
                                        ? 'bg-primary/15 text-primary-text'
                                        : 'bg-muted text-muted-foreground',
                                  )}
                                >
                                  {i + 1}
                                </span>
                                <span
                                  className={cn(
                                    'min-w-0 flex-1 truncate',
                                    i !== current && 'text-muted-foreground',
                                  )}
                                >
                                  {m.title}
                                </span>
                                <span className="h-1 w-11 shrink-0 overflow-hidden rounded-full bg-muted">
                                  <span
                                    className="block h-full rounded-full bg-primary"
                                    style={{ width: `${m.total ? (m.done / m.total) * 100 : 0}%` }}
                                  />
                                </span>
                                <span className="w-7 shrink-0 text-right font-mono text-[11px] tabular-nums text-muted-foreground/70">
                                  {m.done}/{m.total}
                                </span>
                              </button>
                            )
                          })
                        })()}
                      </div>
                    </section>
                  )}

                  <AskCoachThread chat={chat} voice={voice} chips={CHIPS} />
                </div>
              </div>

              <AskCoachComposer
                subject="goal"
                chips={CHIPS}
                chat={chat}
                coachName={coachName}
                onSend={send}
                resetKey={goal._id}
              />
            </div>
          )}
          {goal?.isOwner && goal.goalSlug && (
            <ShareGoalDialog
              goalId={goal._id}
              goalSlug={goal.goalSlug}
              isReadOnly={goal.isReadOnly}
              isPublic={goal.isPublic}
              open={shareOpen}
              onOpenChange={setShareOpen}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}

// Finished goals are the most credible templates: offer to share the plan.
function ShareAsTemplate({ goalId }: { goalId: Id<'nodes'> }) {
  const { t } = useTranslation()
  const save = useMutation(api.templates.saveGoalAsTemplate)
  const [state, setState] = useState<'idle' | 'saving' | 'done'>('idle')
  if (state === 'done')
    return <p className="text-xs font-semibold text-primary-text">{t('templates.sharedToast')}</p>
  return (
    <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5">
      <Layers className="size-4 shrink-0 text-primary-text" />
      <p className="min-w-0 flex-1 text-xs text-foreground">{t('templates.shareFinished')}</p>
      <button
        type="button"
        disabled={state === 'saving'}
        onClick={() => {
          setState('saving')
          save({ goalId })
            .then(() => setState('done'))
            .catch(() => {
              setState('idle')
              toast.error(t('common.error'))
            })
        }}
        className="shrink-0 cursor-pointer rounded-full bg-primary px-3 py-1 text-xs font-bold text-primary-foreground transition-opacity disabled:opacity-50"
      >
        {t('templates.shareCta')}
      </button>
    </div>
  )
}
